import { offsetPagination } from '@/utils/pagination-policy';
import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';
import { reauthenticate } from './reauthenticate';
import {
  requestStepUp,
  STEP_UP_OPERATIONS,
  STEP_UP_REQUIRED_CODE,
  stepUpRequestFrom,
} from './step-up';

let apolloClient: ApolloClient<NormalizedCacheObject>;

// These sign-in steps return auth:mfa_enrollment_required to start the
// enroll-only step; signing out there would end that session.
const ENROLL_ONLY_SIGN_IN_OPERATIONS = ['Authenticate', 'Register'];

/**
 * True when an error means the session can no longer use the app: it
 * expired, or the user's role now requires MFA (grace ended while the app
 * was open). The sign-in page then leads into setup.
 */
export function shouldReauthenticate(
  code: unknown,
  operationName?: string,
): boolean {
  if (code === 'UNAUTHENTICATED') {
    return true;
  }

  return (
    code === 'auth:mfa_enrollment_required' &&
    !ENROLL_ONLY_SIGN_IN_OPERATIONS.includes(operationName ?? '')
  );
}

const logoutLink = onError((error) => {
  const errorsArr = error.graphQLErrors;

  if (
    Array.isArray(errorsArr) &&
    errorsArr.length > 0 &&
    shouldReauthenticate(
      errorsArr[0].extensions?.code,
      error.operation?.operationName,
    )
  ) {
    reauthenticate();
  }
});

/**
 * True when a failed operation should open the step-up prompt: the API
 * wants a recent second factor, the operation is not part of the step-up,
 * and it was not already retried after one.
 */
export function shouldStepUp(
  code: unknown,
  operationName: string | undefined,
  retried: boolean,
): boolean {
  return (
    code === STEP_UP_REQUIRED_CODE &&
    !STEP_UP_OPERATIONS.includes(operationName ?? '') &&
    !retried
  );
}

/**
 * Catches auth:mfa_step_up_required from any operation, opens the step-up
 * prompt and sends the operation again once the user verified. The API
 * checks step-up before the resolver runs, so a retry never repeats a
 * change. If the user cancels, the caller gets the original error.
 */
export const stepUpLink = onError(
  ({ graphQLErrors, operation, forward, response }) => {
    const first = graphQLErrors?.[0];

    if (
      !first ||
      !shouldStepUp(
        first.extensions?.code,
        operation.operationName,
        Boolean(operation.getContext().stepUpRetried),
      )
    ) {
      return undefined;
    }

    return new Observable((observer) => {
      let subscription: { unsubscribe: () => void } | undefined;

      requestStepUp(stepUpRequestFrom(first.extensions))
        .then((passed) => {
          if (!passed) {
            observer.next(response ?? { errors: graphQLErrors });
            observer.complete();
            return undefined;
          }

          operation.setContext({ stepUpRetried: true });
          subscription = forward(operation).subscribe(observer);
          return undefined;
        })
        .catch((err) => observer.error(err));

      return () => subscription?.unsubscribe();
    });
  },
);

const authExpirationLink = new ApolloLink((operation, forward) =>
  forward(operation).map((response) => {
    const expiration = operation
      .getContext()
      .response?.headers?.get('Auth-Expiration');

    if (expiration) {
      localStorage.setItem('authExpires', expiration);
    }

    return response;
  }),
);

export const typePolicies = {
  // A row's id is a marketplace or an organization id, so a marketplace
  // and an organization can share one. Keep the rows inside the report.
  MfaEnrollmentRow: {
    keyFields: false as const,
  },
  ProcedureProfile: {
    fields: {
      procedureDefs: {
        merge: false,
      },
    },
  },
  ProcedureDefinition: {
    fields: {
      medicationProtocols: {
        merge: false,
      },
    },
  },
  ProcedureBaseDefinitionGroup: {
    fields: {
      procedureBaseDefs: {
        merge: false,
      },
    },
  },
  Patient: {
    fields: {
      orders: {
        merge: false,
      },
      vitals: {
        merge: false,
      },
      signedConsentForms: {
        merge: false,
      },
      allergyRecords: {
        merge: false,
      },
      clinicalProcedureRecords: {
        merge: false,
      },
      conditionRecords: {
        merge: false,
      },
      immunizationRecords: {
        merge: false,
      },
      labRecords: {
        merge: false,
      },
      medicationRecords: {
        merge: false,
      },
    },
  },
  FormTemplate: {
    fields: {
      items: {
        merge: false,
      },
    },
  },
  Form: {
    fields: {
      items: {
        merge: false,
      },
      observations: {
        merge: false,
      },
      providers: {
        merge: false,
      },
    },
  },
  ApiKey: {
    fields: {
      roles: {
        merge: false,
      },
    },
  },
  Checkout: {
    fields: {
      paymentLinks: {
        merge: false,
      },
    },
  },
  Organization: {
    fields: {
      appointments: offsetPagination({
        keyArgs: ['page', ['filter', 'sort', 'dateRange']],
        includeArchived: false,
      }),
      clientProfiles: offsetPagination({
        keyArgs: ['page', ['filter', 'sort']],
        includeArchived: false,
      }),
    },
  },
  Marketplace: {
    fields: {
      appointmentRequests: offsetPagination({
        keyArgs: ['page', ['filter', 'sort']],
        includeArchived: false,
      }),
    },
  },
  MarketplaceGroup: {
    fields: {
      marketplaceUsers: offsetPagination({
        keyArgs: ['page', ['filter', 'sort']],
        includeArchived: false,
      }),
    },
  },
  Query: {
    fields: {
      appointmentRequests: offsetPagination({
        keyArgs: ['page', ['filter', 'sort']],
        includeArchived: false,
      }),
      clientProfiles: offsetPagination({
        keyArgs: ['page', ['filter', 'sort']],
        includeArchived: false,
      }),
      marketplaceUsers: offsetPagination({
        keyArgs: ['page', ['filter', 'sort']],
        includeArchived: false,
      }),
    },
  },
};

function createApolloClient(): ApolloClient<NormalizedCacheObject> {
  const httpLink = new HttpLink({
    uri: process.env.NEXT_PUBLIC_API_URL,
    credentials: 'include',
  });

  const link = from([authExpirationLink, logoutLink, stepUpLink, httpLink]);

  return new ApolloClient<NormalizedCacheObject>({
    // Client awareness: sends apollographql-client-name so dispatch-api can
    // attribute created_via=dispatch_admin (tracking plan decision #18).
    name: 'dispatch-web',
    ssrMode: typeof window === 'undefined',
    link,
    cache: new InMemoryCache({
      typePolicies,
    }),
  });
}

export function initializeApollo(
  initialState?: NormalizedCacheObject,
): ApolloClient<NormalizedCacheObject> {
  const client = apolloClient ?? createApolloClient();

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = client.extract();
    // Restore the cache using the data passed from getStaticProps/getServerSideProps
    // combined with the existing cached data
    client.cache.restore({ ...existingCache, ...initialState });
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return client;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = client;

  return client;
}

export function getApolloClient(): ApolloClient<NormalizedCacheObject> {
  return apolloClient;
}
